import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { _LiveLogPage } from './LiveLogPage'
import { ModalOverlay } from 'common/styleguide/ModalOverlay'
import { CTA } from 'common/styleguide/CTA'
import { fulfilled } from 'lib/promiseMiddlewareHelpers'
import { LOAD as LOAD_COMMENTS } from 'lib/api/comments'
import { trackpointMetrics } from 'lib/metricsCalculator'
import { promiseMapAll, getCurrentUser } from 'lib/utils'
import { Msg, commonCTAs } from 'lib/localization'
import { LiveLog } from 'data/records'
import { tripActions } from 'data/resources/trips'
import { routeActions } from 'data/resources/routes'
import moment from 'moment'
import { FormattedMessage as T } from 'react-intl'

const TRIP_ID = 13972523
const ROUTE_ID = 30569893

const allComments = [
  {
    elapsed: 3600 + 37 * 60,
    comment: {
      id: 1,
      comment: <T id='llDemo.comment1' defaultMessage='Awesome schoolhouse!' />,
      user: {
        id: 729059,
        name: 'Jordan F',
        highlighted_photo_id: 2201772
      }
    }
  },
  {
    elapsed: 2 * 3600 + 42 * 60,
    comment: {
      id: 2,
      comment: <T id='llDemo.comment2' defaultMessage='Jewel Road is the best!' />,
      user: {
        id: 42,
        name: 'Kevin Purdy',
        highlighted_photo_id: 1500865
      }
    }
  },
  {
    elapsed: 4 * 3600 + 18 * 60,
    comment: {
      id: 3,
      comment: <T id='llDemo.comment3' defaultMessage='The Pleasant Ridge switchbacks are so fun!' />,
      user: {
        id: 426184,
        name: 'William Laubernds',
        highlighted_photo_id: 2720628
      }
    }
  },
  {
    elapsed: 5 * 3600 + 6 * 60,
    comment: {
      id: 4,
      comment: <T id='llDemo.comment4' defaultMessage='You are so close to tacos!' />,
      user: {
        id: 889915,
        name: 'molly.sugar',
        highlighted_photo_id: 4853254
      }
    }
  }
]
const currentComments = (time, firstTime) => {
  const currentElapsed = time - firstTime
  return allComments.filter(x => x.elapsed < currentElapsed)
    .map(({ elapsed, comment }) => ({
      ...comment,
      created_at: moment().subtract(currentElapsed - elapsed, 'seconds')
    }))
}

export const _LiveLogDemo = ({ currentUser, loadRoute, loadTrip, setComments }) => {
  const [liveLog, setLiveLog] = useState()
  const [user, setUser] = useState()
  const [trip, setTrip] = useState()
  const [troute, setTRoute] = useState()
  const [pointsOfInterest, setPointsOfInterest] = useState()
  const [photos, setPhotos] = useState()
  useEffect(() => {
    let commentsTimer
    promiseMapAll({
      route: loadRoute(ROUTE_ID),
      trip: loadTrip(TRIP_ID)
    }).then(({ route: { value: routeResponse }, trip: { value: tripResponse } }) => {
      const { trip } = tripResponse
      const { route } = routeResponse

      // these won't change through the demo, so just initialize them once
      setUser(tripResponse.extras.find(x => x.type === 'user').user)
      setTRoute(route)
      setPointsOfInterest(routeResponse.extras.filter(x => x.type === 'point_of_interest').map(x => x.point_of_interest))

      const firstTime = trip.trackPoints[0].time
      const lastTime = trip.trackPoints[trip.trackPoints.length - 1].time
      const duration = lastTime - firstTime
      const updateDemo = () => {
        // walk through 10% to 90% of trip in real time on repeat.
        const time = firstTime + duration * 0.1 + (((new Date()).getTime() / 1000 << 0) % (duration * 0.8))
        const newRawPoints = trip.rawPoints.filter(x => x.t < time)
        const newTrackPoints = trackpointMetrics(newRawPoints)

        const newPhotos = tripResponse.extras.filter(x => x.type === 'photo' && moment(x.photo.capturedAt).isBefore(time * 1000)).map(x => x.photo)
        setPhotos(newPhotos)

        // metrics calculator looks at providedDistance, usually it is set in legacy JS (points.js) that we're not using here
        newTrackPoints.forEach(pt => { pt.providedDistance = pt.distance })
        const mc = new window.rwgps.MetricsCalculator(newTrackPoints, true)
        const metrics = mc.calculate()

        setTrip(trip.withMutations(x => {
          x.set('rawPoints', newRawPoints)
            .set('trackPoints', newTrackPoints)
            .set('photoIds', newPhotos.map(p => p.id))
            .set('distance', metrics.distance)
            .set('calories', null)
            .set('elevationGain', metrics.ele_gain)
            .set('elevationLoss', metrics.ele_loss)
            .set('avgSpeed', metrics.speed.avg)
            .set('movingTime', metrics.movingTime)
            .set('metrics', metrics)
        }))

        if (commentsTimer) {
          clearInterval(commentsTimer)
        } else {
          commentsTimer = setInterval(() => setComments(currentComments(time, firstTime)), 500)
        }

        setLiveLog(LiveLog({
          finalized: false,
          updatedAt: moment(),
          assetType: 'Route',
          assetId: route.id,
          distanceAlong: newTrackPoints[newTrackPoints.length - 1].distance,
          eta: moment().add(lastTime - time, 'seconds'),
          battery: Math.random(),
          signal: 3 // good
        }))
      }
      updateDemo()
      const timer = setInterval(updateDemo, 60000)
      return () => { clearInterval(timer) }
    })
  }, [])

  const [showInfoModal, setShowInfoModal] = useState()
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowInfoModal(true)
    }, 15000)
    return () => { clearTimeout(timer) }
  }, [])

  if (!liveLog) {
    return <div />
  }

  return (
    <React.Fragment>
      <_LiveLogPage
        {...{ liveLog, user, trip, troute, pointsOfInterest, photos, currentUser, loadLiveLog: () => Promise.resolve(), loadComments: () => Promise.resolve() }}
        readOnly />
      {showInfoModal && <InfoModal onRequestClose={() => { setShowInfoModal(false) }} />}
    </React.Fragment>
  )
}

const InfoModal = ({ onRequestClose }) => {
  const currentUser = getCurrentUser()
  const showUpgrade = (!currentUser.id || currentUser.accountLevel === 0)

  return (
    <ModalOverlay
      maxWidth={500}
      onRequestClose={onRequestClose}
      title={<T id='llDemo.sayGoodbye' defaultMessage='Say Goodbye to Guesswork' />}
      subtitle={<T id='llDemo.useLL' defaultMessage='Use Live Logging the next time you go out.' />}>
      <h3><T id='llDemo.estCompletionTime' defaultMessage='Estimated Completion Time' /></h3>
      <p><T id='llDemo.whenNavigating' defaultMessage='When navigating, the estimated time to finish updates as you ride in response to how fast you ride. Getting tired towards the end? Your ETA will change to reflect it.' /></p>
      <h3><T id='llDemo.realtimeComments' defaultMessage='Real Time Comments' /></h3>
      <p><T id='llDemo.commentsMade' defaultMessage='Comments made on the Live Logging page will be spoken out loud while you ride. Enjoy some motivation from your friends!' /></p>
      <h3><T id='llDemo.routeEleProfile' defaultMessage='Route + Elevation Profile' /></h3>
      <p><T id='llDemo.routeVisible' defaultMessage='The route you are navigating is now visible, along with an interactive elevation profile to analyze comprehensive ride stats.' /></p>
      <h3><T id='llDemo.photoSupport' defaultMessage='Photo Support' /></h3>
      <p><T id='llDemo.photoUpload' defaultMessage='The app can optionally upload geo-tagged photos taken while you ride, so the link you share will contain where you have gone, where you are going, and what you have seen along the way.' /></p>
      <CTA secondary={showUpgrade} href='https://ridewithgps.com/help/live-logging' target='_blank'><Msg m={commonCTAs.learnMore} /></CTA>
      {showUpgrade && <CTA href='/plans' onClick={onRequestClose}><Msg m={commonCTAs.upgradeNow} /></CTA>}
      <div style={{ height: '25px' }} />
    </ModalOverlay>
  )
}

export const LiveLogDemo = connect(
  ({ currentUser }) => ({ currentUser }),
  dispatch => ({
    loadRoute: id => dispatch(routeActions.read(id)),
    loadTrip: id => dispatch(tripActions.read(id)),
    setComments: comments => dispatch({
      type: fulfilled(LOAD_COMMENTS),
      meta: { parentType: 'trip', parentId: TRIP_ID },
      payload: { comments }
    })
  })
)(_LiveLogDemo)
